import { useSelector } from 'react-redux';
import { usePermissions as usePermissionsContext } from 'permissions/PermissionContext';

/**
 * Redux state type for auth
 */
interface AuthState {
    auth?: {
        teacher?: {
            role?: number;
        };
    };
}

/**
 * Return type for usePermissions hook
 */
export interface UsePermissionsReturn {
    hasPermission: (permission: string) => boolean;
    hasAnyPermission: (permissions: string[]) => boolean;
    hasAllPermissions: (permissions: string[]) => boolean;
    hasRole: (requiredRole: number | number[]) => boolean;
    hasAnyRole: (roles: number[]) => boolean;
    hasRoleOrHigher: (minRole: number) => boolean;
    currentRole?: number;
}

/**
 * Enhanced permission checking hook
 * Extends the existing PermissionContext with additional functionality
 * Note: This extends the base usePermissions from PermissionContext
 */
export const usePermissions = (): UsePermissionsReturn => {
    const { hasPermission: hasPermissionFromContext } = usePermissionsContext();
    const auth = useSelector((state: AuthState) => state.auth);
    const role = auth?.teacher?.role;

    /**
     * Check if user has a specific permission
     */
    const hasPermission = (permission: string): boolean => {
        return hasPermissionFromContext(permission);
    };

    /**
     * Check if user has any of the specified permissions (OR logic)
     */
    const hasAnyPermission = (permissions: string[]): boolean => {
        if (!permissions || permissions.length === 0) return true;
        return permissions.some(hasPermission);
    };

    /**
     * Check if user has all of the specified permissions (AND logic)
     */
    const hasAllPermissions = (permissions: string[]): boolean => {
        if (!permissions || permissions.length === 0) return true;
        return permissions.every(hasPermission);
    };

    /**
     * Check if user has a specific role
     */
    const hasRole = (requiredRole: number | number[]): boolean => {
        if (!role) return false;
        if (Array.isArray(requiredRole)) {
            return requiredRole.includes(role);
        }
        return role === requiredRole;
    };

    /**
     * Check if user has any of the specified roles
     */
    const hasAnyRole = (roles: number[]): boolean => {
        if (!role || !roles || roles.length === 0) return false;
        return roles.includes(role);
    };

    /**
     * Check if user has role or higher privilege
     * Lower number = higher privilege (1 = ADMIN, 4 = TEACHER)
     */
    const hasRoleOrHigher = (minRole: number): boolean => {
        if (!role) return false;
        return role <= minRole;
    };

    return {
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        hasRole,
        hasAnyRole,
        hasRoleOrHigher,
        currentRole: role,
    };
};
